/**
 * Настройки OIDC для Zitadel.
 *
 * Authorization Code + PKCE, публичный клиент. Секрета у фронтенда нет и
 * быть не должно: всё в этом файле попадает в бандл и видно любому.
 */

import type { UserManagerSettings } from 'oidc-client-ts';

const authority = process.env.NEXT_PUBLIC_OIDC_AUTHORITY ?? '';
const clientId = process.env.NEXT_PUBLIC_OIDC_CLIENT_ID ?? '';
const projectId = process.env.NEXT_PUBLIC_OIDC_PROJECT_ID ?? '';
const redirectUri = process.env.NEXT_PUBLIC_OIDC_REDIRECT_URI ?? '';
const postLogoutRedirectUri = process.env.NEXT_PUBLIC_OIDC_POST_LOGOUT_REDIRECT_URI ?? '';

/**
 * Два последних scope — не украшение, без них вход проходит, а API отвечает 401:
 *
 * - `...:aud` кладёт проект в audience токена. Бэкенд проверяет, что токен
 *   выписан именно нам, иначе токен от чужого приложения проходил бы как свой.
 * - `resourceowner` кладёт в токен организацию. Без неё бэкенд не поймёт,
 *   к какому юрлицу относится человек, и не найдёт тенант.
 *
 * `offline_access` здесь нет намеренно: refresh-токен Zitadel выдаёт, только
 * если в приложении разрешён grant Refresh Token. Просить его у приложения,
 * где он выключен, — получить отказ на самом входе. Включите grant и
 * добавьте scope через NEXT_PUBLIC_OIDC_SCOPES, если нужен молчаливый
 * продлённый сеанс.
 */
export const defaultScopes = [
  'openid',
  'profile',
  'email',
  'urn:zitadel:iam:user:resourceowner',
  `urn:zitadel:iam:org:project:id:${projectId}:aud`,
].join(' ');

export const oidcScopes = process.env.NEXT_PUBLIC_OIDC_SCOPES || defaultScopes;

/** Куда вернуть человека после входа. Кладётся в state запроса авторизации. */
export interface SigninState {
  returnTo?: string;
}

export const oidcSettings: UserManagerSettings = {
  authority,
  client_id: clientId,
  redirect_uri: redirectUri,
  post_logout_redirect_uri: postLogoutRedirectUri,
  response_type: 'code',
  scope: oidcScopes,

  /**
   * Профиль пользователя добираем через /api/me, а не из userinfo: имя и
   * почта нужны нам вместе с тенантом и грантами, одним ответом.
   */
  loadUserInfo: false,

  /**
   * Хранилища оставлены по умолчанию (токен — sessionStorage, state —
   * localStorage). Токен умирает вместе со вкладкой, и это то, что нужно:
   * гранты нельзя кэшировать дольше сессии — человека переводят между
   * подразделениями, и вчерашняя область видимости уже неверна.
   */
};

/** Настроено ли окружение. Пустой authority даёт невнятный сбой глубоко внутри. */
export function missingOidcConfig(): string[] {
  const missing: string[] = [];
  if (!authority) missing.push('NEXT_PUBLIC_OIDC_AUTHORITY');
  if (!clientId) missing.push('NEXT_PUBLIC_OIDC_CLIENT_ID');
  if (!projectId) missing.push('NEXT_PUBLIC_OIDC_PROJECT_ID');
  if (!redirectUri) missing.push('NEXT_PUBLIC_OIDC_REDIRECT_URI');
  return missing;
}
